import React, { useState } from 'react'
import styles from './style/configuration_card_style.module.scss'
import CopyIcon from './assets/copy-icon.svg'
import DownloadIcon from './assets/download-icon.svg'
import TrashIcon from './assets/trash-icon.svg'
import { capitalizeFirstLetter } from '../../../../helpers'
import { handlePlanPdfDownload } from '../../utils'
import LocalizedLink from '../../../global/LocalizedLink'
import Modal from '../../../global/Modal'
import confirm from '../../../global/extendedConfirm'
import Button from '../../../global/Button'
import Translation from '../../../global/Translation'
import moment from 'moment/moment'

const IN_DECIMALS = 2
const DASH = '-'
const DOWNLOAD_DELAY_MS = 600

const joinOrDash = (parts) => parts.filter(Boolean).join(' / ') || DASH
const trans = (intl, id, defaultMessage) => intl.formatMessage({ id, defaultMessage })

const getColor = (material, intl) => {
    const colorName = material.colorName || ''
    const finishName = material.finish
        ? intl.formatMessage({ id: material.finish })
        : (material.finishName || '')

    return joinOrDash([capitalizeFirstLetter(colorName), capitalizeFirstLetter(finishName)])
}

const getDimensions = (table, measureFromMetric, units) => {
    if (table.length && table.width) {
        return measureFromMetric(table.length, IN_DECIMALS) +
            ' x ' +
            measureFromMetric(table.width, IN_DECIMALS) +
            ' ' + units
    }

    return table.dimension || DASH
}

const getQuantity = (quantity) => quantity ? String(quantity) + ' ud' : DASH

const getCardPayload = (item) => {
    if (item.grouped) {
        return {
            product: 'flooring-skirting',
            model: 'flooring',
            flooring: item.flooring ? item.flooring.payload || {} : {},
            skirting: item.skirting ? item.skirting.payload || {} : {}
        }
    }

    return item.payload || {}
}

const getPlanItems = (item) => {
    if (item.grouped) {
        return [item.flooring, item.skirting].filter((configuration) => configuration && configuration.drawingUrl)
    }

    return item.drawingUrl ? [item] : []
}

const downloadPlan = (configuration, model) => {
    const url =
        process.env.REACT_APP_API_HOST +
        '/helper/view-pdf-url/?url=' +
        encodeURIComponent(configuration.drawingUrl)

    handlePlanPdfDownload(url, model || (configuration.payload || {}).model, configuration.ref)
}

const downloadPlans = (planItems, index = 0) => {
    if (!planItems[index]) return

    downloadPlan(planItems[index])

    if (planItems[index + 1]) {
        setTimeout(() => downloadPlans(planItems, index + 1), DOWNLOAD_DELAY_MS)
    }
}

const getRouteParams = (item, payload) => {
    if (item.grouped) {
        return {
            type: 'flooring',
            model: 'flooring',
            fabricator: !!item.thirdParty ? 'local' : 'global',
            refcode: item.parentRef
        }
    }

    return {
        type: payload.product,
        model: payload.model.toLowerCase().replace(' ', '_'),
        fabricator: !!item.thirdParty ? 'local' : 'global',
        refcode: item.ref
    }
}

const SPEC_BUILDERS = {
    washbasins: (p, { measureFromMetric, units, intl }) => {
        const material = p.material || {}
        const countertop = p.countertop || {}
        const skirt = countertop.skirt || {}
        const basins = p.basins || {}
        const tapDrills = p.tapDrills || {}
        const towelRails = p.towelRails || {}
        const soapHolders = p.soapHolders || {}
        const productName = p.product || ''
        const translatedProductName = productName ? trans(intl, productName, productName) : ''
        const modelName = p.model || ''

        const color = getColor(material, intl)

        let countertopSize = DASH
        if (countertop.length && countertop.width) {
            countertopSize =
                measureFromMetric(countertop.length, IN_DECIMALS) +
                ' x ' +
                measureFromMetric(countertop.width, IN_DECIMALS) +
                ' ' + units
        } else if (countertop.dimension) {
            countertopSize = countertop.dimension
        }

        let edge = DASH
        if (skirt.hasSkirt) {
            const skirtLength = skirt.length
                ? measureFromMetric(skirt.length, IN_DECIMALS) + ' ' + units
                : ''
            const translatedSides = Array.isArray(skirt.sides)
                ? skirt.sides
                    .map((side) => {
                        const sideValue = String(side || '').toLowerCase()
                        const defaultMessage = sideValue === 'left'
                            ? 'left'
                            : sideValue === 'right'
                                ? 'right'
                                : String(side || '')

                        return trans(intl, sideValue, defaultMessage)
                    })
                    .join(', ')
                : ''

            const sides = skirt.sides ? trans(intl, 'sides', 'Sides') + ': ' + translatedSides.toLowerCase() : ''
            edge = joinOrDash([skirtLength, sides])
        }

        const basinsCount = basins && basins.items && basins.items.length ? basins.items.length : null

        const extrasParts = []
        if (tapDrills && tapDrills.items && tapDrills.items.length) {
            extrasParts.push(trans(intl, 'tap_drills', 'Tap drills') + ': ' + tapDrills.items.length)
        }
        if (towelRails && towelRails.items && towelRails.items.length) {
            const sizes = towelRails.items
                .filter((i) => i.length)
                .map((i) => measureFromMetric(i.length, IN_DECIMALS) + ' ' + units)
                .join(', ')
            let str = trans(intl, 'towel_rails', 'Towel rails') + ': ' + towelRails.items.length
            if (sizes) str += ' (' + sizes + ')'
            extrasParts.push(str)
        }
        if (soapHolders && soapHolders.items && soapHolders.items.length) {
            extrasParts.push(trans(intl, 'soap_holders', 'Soap holders') + ': ' + soapHolders.items.length)
        }

        const product = joinOrDash([translatedProductName, capitalizeFirstLetter(modelName)])

        return [
            {
                labelId: 'product', labelDefault: 'Product',
                label: trans(intl, 'product', 'Product'),
                value: product
            },
            {
                labelId: 'color', labelDefault: 'Color',
                label: trans(intl, 'color', 'Color'),
                value: color
            },
            {
                labelId: 'countertop', labelDefault: 'Countertop',
                label: trans(intl, 'countertop', 'Countertop'),
                value: countertopSize
            },
            {
                labelId: 'edge', labelDefault: 'Edge',
                label: trans(intl, 'edge', 'Edge'),
                value: edge
            },
            {
                labelId: 'basins', labelDefault: 'Basins',
                label: trans(intl, 'basins', 'Basins'),
                value: basinsCount !== null ? String(basinsCount) : null,
                hidden: basinsCount === null
            },
            {
                labelId: 'extras', labelDefault: 'Extras',
                label: trans(intl, 'extras', 'Extras'),
                value: extrasParts.join(' / ') || null,
                hidden: extrasParts.length === 0,
                extraClass: styles['config-card__spec--extras']
            }
        ]
    },

    showertray: (p, { measureFromMetric, units, intl }) => {
        const material = p.material || {}
        const tray = p.tray || p.countertop || {}
        const drain = p.drain || {}
        const tapDrills = p.tapDrills || {}
        const towelRails = p.towelRails || {}
        const soapHolders = p.soapHolders || {}
        const productName = p.product || ''
        const translatedProductName = productName ? trans(intl, productName, productName) : ''
        const modelName = p.model || ''

        const extrasParts = []
        if (tapDrills && tapDrills.items && tapDrills.items.length) {
            extrasParts.push(trans(intl, 'tap_drills', 'Tap drills') + ': ' + tapDrills.items.length)
        }
        if (towelRails && towelRails.items && towelRails.items.length) {
            const sizes = towelRails.items
                .filter((i) => i.length)
                .map((i) => measureFromMetric(i.length, IN_DECIMALS) + ' ' + units)
                .join(', ')
            let str = trans(intl, 'towel_rails', 'Towel rails') + ': ' + towelRails.items.length
            if (sizes) str += ' (' + sizes + ')'
            extrasParts.push(str)
        }
        if (soapHolders && soapHolders.items && soapHolders.items.length) {
            extrasParts.push(trans(intl, 'soap_holders', 'Soap holders') + ': ' + soapHolders.items.length)
        }

        const product = joinOrDash([translatedProductName, capitalizeFirstLetter(modelName)])
        const drainPosition = drain.position || drain.type || null

        return [
             {
                labelId: 'product', labelDefault: 'Product',
                label: trans(intl, 'product', 'Product'),
                value: product
            },
            {
                labelId: 'color', labelDefault: 'Color',
                label: trans(intl, 'color', 'Color'),
                value: getColor(material, intl)
            },
            {
                labelId: 'dimensions', labelDefault: 'Dimensions',
                label: trans(intl, 'dimensions', 'Dimensions'),
                value: getDimensions(tray, measureFromMetric, units)
            },
            {
                labelId: 'drain_position', labelDefault: 'Drain position',
                label: trans(intl, 'drain_position', 'Drain position'),
                value: drainPosition,
                hidden: !drainPosition
            },
            {
                labelId: 'extra', labelDefault: 'Extra',
                label: trans(intl, 'extra', 'Extra'),
                value: extrasParts.join(' / ') || null,
                hidden: extrasParts.length === 0,
                extraClass: styles['config-card__spec--extras']
            }
        ]
    },

    cladding: (p, { measureFromMetric, units, intl }) => {
        const material = p.material || {}
        const table = p.table || {}

        const extrasParts = []
        if (table.meshed) {
            extrasParts.push(trans(intl, 'meshed', 'Meshed'))
        }

        return [
            {
                labelId: 'color',
                labelDefault: 'Color',
                label: trans(intl, 'color', 'Color'),
                value: getColor(material, intl)
            },
            {
                labelId: 'dimensions',
                labelDefault: 'Dimensions',
                label: trans(intl, 'dimensions', 'Dimensions'),
                value: getDimensions(table, measureFromMetric, units)
            },
            {
                labelId: 'thickness',
                labelDefault: 'Thickness',
                label: trans(intl, 'thickness', 'Thickness'),
                value: table.thickness ? table.thickness + ' cm' : DASH
            },
            {
                labelId: 'qty',
                labelDefault: 'Quantity',
                label: trans(intl, 'qty', 'Quantity'),
                value: getQuantity(p.quantity)
            },
            {
                labelId: 'extras',
                labelDefault: 'Extras',
                label: trans(intl, 'extras', 'Extras'),
                value: extrasParts.join(' / ') || null,
                hidden: extrasParts.length === 0,
                extraClass: styles['config-card__spec--extras']
            }
        ]
    },

    flooring: (p, { measureFromMetric, units, intl }) => {
        const material = p.material || {}
        const table = p.table || {}

        const extrasParts = []
        if (table.meshed) {
            extrasParts.push(trans(intl, 'meshed', 'Meshed'))
        }
        if (table.cutting && table.cutting.veinDirection) {
            const veinDirection = table.cutting.veinDirection === 'horizontal'
                ? trans(intl, 'horizontal', 'Horizontal')
                : trans(intl, 'vertical', 'Vertical')
            extrasParts.push(trans(intl, 'vein_direction', 'Vein direction') + ': ' + veinDirection)
        }

        return [
            {
                labelId: 'product',
                labelDefault: 'Product',
                label: trans(intl, 'product', 'Product'),
                value: trans(intl, 'flooring', 'Flooring')
            },
            {
                labelId: 'color',
                labelDefault: 'Color',
                label: trans(intl, 'color', 'Color'),
                value: getColor(material, intl)
            },
            {
                labelId: 'dimensions',
                labelDefault: 'Dimensions',
                label: trans(intl, 'dimensions', 'Dimensions'),
                value: getDimensions(table, measureFromMetric, units)
            },
            {
                labelId: 'thickness',
                labelDefault: 'Thickness',
                label: trans(intl, 'thickness', 'Thickness'),
                value: table.thickness ? table.thickness + ' cm' : DASH
            },
            {
                labelId: 'qty',
                labelDefault: 'Quantity',
                label: trans(intl, 'qty', 'Quantity'),
                value: getQuantity(p.quantity)
            },
            {
                labelId: 'extras',
                labelDefault: 'Extras',
                label: trans(intl, 'extras', 'Extras'),
                value: extrasParts.join(' / ') || null,
                hidden: extrasParts.length === 0,
                extraClass: styles['config-card__spec--extras']
            }
        ]
    },

    skirting: (p, { measureFromMetric, units, intl }) => {
        const material = p.material || {}
        const table = p.table || {}
        const format = table.width && table.thickness
            ? measureFromMetric(table.width, IN_DECIMALS) +
                ' x ' +
                measureFromMetric(table.thickness, IN_DECIMALS) +
                ' ' + units
            : (table.dimension || DASH)

        return [
            {
                labelId: 'product',
                labelDefault: 'Product',
                label: trans(intl, 'product', 'Product'),
                value: trans(intl, 'skirting', 'Skirting')
            },
            {
                labelId: 'color',
                labelDefault: 'Color',
                label: trans(intl, 'color', 'Color'),
                value: getColor(material, intl)
            },
            {
                labelId: 'length',
                labelDefault: 'Length',
                label: trans(intl, 'length', 'Length'),
                value: table.length ? measureFromMetric(table.length, IN_DECIMALS) + ' ' + units : DASH
            },
            {
                labelId: 'format',
                labelDefault: 'Format',
                label: trans(intl, 'format', 'Format'),
                value: format
            },
            {
                labelId: 'qty',
                labelDefault: 'Quantity',
                label: trans(intl, 'qty', 'Quantity'),
                value: getQuantity(p.quantity)
            }
        ]
    },

    'flooring-skirting': (p, { measureFromMetric, units, intl }) => {
        const flooring = p.flooring || {}
        const skirting = p.skirting || {}
        const flooringTable = flooring.table || {}
        const skirtingTable = skirting.table || {}
        const skirtingFormat = skirtingTable.width && skirtingTable.thickness
            ? measureFromMetric(skirtingTable.width, IN_DECIMALS) +
                ' x ' +
                measureFromMetric(skirtingTable.thickness, IN_DECIMALS) +
                ' ' + units
            : (skirtingTable.dimension || DASH)
        const flooringExtras = []

        if (flooringTable.meshed) {
            flooringExtras.push(trans(intl, 'meshed', 'Meshed'))
        }
        if (flooringTable.cutting && flooringTable.cutting.veinDirection) {
            const veinDirection = flooringTable.cutting.veinDirection === 'horizontal'
                ? trans(intl, 'horizontal', 'Horizontal')
                : trans(intl, 'vertical', 'Vertical')
            flooringExtras.push(trans(intl, 'vein_direction', 'Vein direction') + ': ' + veinDirection)
        }

        return [
            {
                labelId: 'flooring_product',
                labelDefault: 'Product',
                label: trans(intl, 'product', 'Product'),
                value: trans(intl, 'soleria', 'Flooring')
            },
            {
                labelId: 'flooring_color',
                labelDefault: 'Color',
                label: trans(intl, 'color', 'Color'),
                value: getColor(flooring.material || {}, intl)
            },
            {
                labelId: 'flooring_measures',
                labelDefault: 'Measures',
                label: trans(intl, 'measures', 'Measures'),
                value: joinOrDash([
                    getDimensions(flooringTable, measureFromMetric, units),
                    flooringTable.thickness ? flooringTable.thickness + ' cm' : null
                ])
            },
            {
                labelId: 'flooring_extras',
                labelDefault: 'Extras',
                label: trans(intl, 'extras', 'Extras'),
                value: flooringExtras.join(' / ') || null,
                hidden: flooringExtras.length === 0,
                extraClass: styles['config-card__spec--extras']
            },
            {
                labelId: 'flooring_skirting_separator',
                separator: true
            },
            {
                labelId: 'skirting_product',
                labelDefault: 'Product',
                label: trans(intl, 'product', 'Product'),
                value: trans(intl, 'rodapies', 'Skirting')
            },
            {
                labelId: 'skirting_color',
                labelDefault: 'Color',
                label: trans(intl, 'color', 'Color'),
                value: getColor(skirting.material || {}, intl)
            },
            {
                labelId: 'skirting_measures',
                labelDefault: 'Measures',
                label: trans(intl, 'measures', 'Measures'),
                value: joinOrDash([
                    skirtingTable.length ? measureFromMetric(skirtingTable.length, IN_DECIMALS) + ' ' + units : null,
                    skirtingFormat
                ])
            }
        ]
    }
}

const buildGenericSpecs = (p, { intl }) => {
    const material = p.material || {}
    return [
        {
            labelId: 'color', labelDefault: 'Color',
            label: trans(intl, 'color', 'Color'),
            value: getColor(material, intl)
        }
    ]
}

const getSpecs = (payload, productType, ctx) => {
    const builder = SPEC_BUILDERS[productType] || buildGenericSpecs
    return builder(payload, ctx).filter((spec) => !spec.hidden)
}

const ConfigurationCard = (props) => {
    const {
        item,
        removeFavoriteConfiguration,
        fetchFavoriteConfigurations,
        cloneConfiguration,
        cloneGroupedConfiguration,
        pageFavoriteConfig,
        sizeFavoriteConfig,
        measureFromMetric,
        units,
        intl,
        triggerCopyConfiguration,
        triggerDeleteConfiguration
    } = props

    const [showCloneModal, setShowCloneModal] = useState(false)
    const [newConfigName, setNewConfigName] = useState('')
    const [isCloning, setIsCloning] = useState(false)

    const handleOpenCloneModal = () => {
        setNewConfigName('')
        setShowCloneModal(true)
    }
    const handleCloseCloneModal = () => {
        setShowCloneModal(false)
        setNewConfigName('')
    }

    const handleCloneConfirm = () => {
        if (!newConfigName.trim()) return
        const cloneAction = item.grouped ? cloneGroupedConfiguration : cloneConfiguration
        const cloneRef = item.grouped ? item.parentRef : item.ref
        if (!cloneRef || !cloneAction) return

        setIsCloning(true)
        cloneAction(cloneRef, newConfigName.trim())
            .then(() => {
                fetchFavoriteConfigurations(pageFavoriteConfig, sizeFavoriteConfig)
                handleCloseCloneModal()
            })
            .finally(() => setIsCloning(false))
    }

    const handlePlanDownload = () => {
        downloadPlans(getPlanItems(item))
    }

    const handleRemoveFavoriteConfig = () =>
        confirm(
            trans(intl, 'are_you_sure_you_want_to_delete_the_configuration', 'Are you sure you want to delete the configuration?'),
            {
                isV2: true,
                title: trans(intl, 'delete_this_configuration', 'Delete this configuration'),
                textCancel: trans(intl, 'cancel', 'Cancel'),
                textOk: trans(intl, 'delete', 'Delete'),
                advise: trans(intl, 'this_action_is_permanent_and_you_will_not_be_able_to_recover_your_saved_settings', 'This action is permanent and you will not be able to recover your saved settings.')
            }
        ).then(
            () => {
                const refs = item.grouped
                    ? [item.flooring, item.skirting].filter(Boolean).map((configuration) => configuration.ref)
                    : [item.ref]

                triggerDeleteConfiguration()
                return Promise.all(refs.map((ref) => removeFavoriteConfiguration(ref))).then(() =>
                    fetchFavoriteConfigurations(pageFavoriteConfig, sizeFavoriteConfig)
                )
            },
            () => Promise.reject()
        )

    const p = getCardPayload(item)
    const ctx = { measureFromMetric, units, intl }
    const specs = getSpecs(p, p.product, ctx)
    const routeParams = getRouteParams(item, p)
    const productImages = item.grouped
        ? [p.flooring.render_picture_url, p.skirting.render_picture_url].filter(Boolean)
        : [p.render_picture_url || item.defaultImageModelUrl].filter(Boolean)
    const imageUrls = productImages.length > 0 ? productImages : [item.defaultImageModelUrl].filter(Boolean)
    const showGroupedImages = imageUrls.length > 1
    const imageAlt = p.product ? trans(intl, p.product, p.product) : item.configName || ''

    return (
        <article className={styles['config-card']}>
            <div className={[
                styles['config-card__image-wrapper'],
                showGroupedImages && styles['config-card__image-wrapper--grouped']
            ].filter(Boolean).join(' ')}>
                {imageUrls.map((imageUrl, index) => (
                    <img
                        key={imageUrl}
                        src={imageUrl}
                        alt={capitalizeFirstLetter(imageAlt) + (showGroupedImages ? ' ' + (index + 1) : '')}
                        className={[
                            styles['config-card__image'],
                            showGroupedImages && styles['config-card__image--grouped']
                        ].filter(Boolean).join(' ')}
                    />
                ))}
            </div>

            <div className={styles['config-card__body']}>
                <h2 className={styles['config-card__title']}>
                    {item.configName || 'Config #' + item.id}
                </h2>

                <div className={styles['config-card__actions']}>
                    <div>
                        <span className={styles['config-card__meta']}>
                            {moment(item.createdAt).format('L')}
                        </span>
                    </div>
                    <div className={styles['config-card__action-buttons']}>
                        {getPlanItems(item).length > 0 && (
                            <button
                                className={styles['config-card__action-btn']}
                                onClick={handlePlanDownload}
                                gtm-label="saved-custom-drafts_button_download-custom-draft"
                            >
                                <img className="download-icon" alt="Download" src={DownloadIcon} />
                            </button>
                        )}
                        <button
                            className={styles['config-card__action-btn']}
                            onClick={() => {
                                handleOpenCloneModal()
                            }}
                            gtm-label="saved-custom-drafts_button_copy-custom-draft"
                        >
                            <img className="copy-icon" alt="Copy icon" src={CopyIcon} />
                        </button>
                        <button
                            className={styles['config-card__action-btn']}
                            onClick={() => {
                                handleRemoveFavoriteConfig()
                            }}
                            gtm-label="saved-custom-drafts_button_delete-custom-draft"
                        >
                            <img className="trash-icon" alt="Trash icon" src={TrashIcon} />
                        </button>
                    </div>
                </div>

                <ul className={styles['config-card__specs']}>
                    {specs.map((spec) => (
                        <li
                            key={spec.labelId}
                            className={[styles['config-card__spec'], spec.separator && styles['config-card__spec--separator'], spec.extraClass]
                                .filter(Boolean)
                                .join(' ')}
                        >
                            {!spec.separator && (
                                <React.Fragment>
                                    <span>{spec.label}:</span>{' '}
                                    {spec.value}
                                </React.Fragment>
                            )}
                        </li>
                    ))}
                </ul>
            </div>

            <div className={styles['config-card__footer']}>
                <LocalizedLink
                    routeId="ROUTE_CONFIG_CUT_TO_SIZE"
                    params={routeParams}
                >
                    <button className={styles['config-card__cta']} gtm-label="saved-custom-drafts_link_re-edit">
                        <Translation id="continue_configuring" defaultMessage="Continue configuring" />{' '}
                        <i className="fas fa-chevron-right" />
                    </button>
                </LocalizedLink>
            </div>

            {showCloneModal && (
                <Modal
                    title={trans(intl, 'duplicate_this_configuration', 'Duplicate this configuration')}
                    onClose={handleCloseCloneModal}
                    size="small"
                    customClass={styles['clone-modal']}
                    footer={
                        <React.Fragment>
                            <button
                                className={styles['clone-modal__cancel-btn']}
                                onClick={handleCloseCloneModal}
                                disabled={isCloning}
                            >
                                <Translation id={'cancel'} defaultMessage={'Cancel'} />
                            </button>
                            <Button
                                inverted
                                onClick={() => {
                                    handleCloneConfirm()
                                    triggerCopyConfiguration()
                                }}
                                disabled={!newConfigName.trim() || isCloning}
                            >
                                <Translation id={'save'} defaultMessage={'Save'} />
                            </Button>
                        </React.Fragment>
                    }
                    customClassFooter={styles['clone-modal__footer']}
                >
                    <p className={styles['clone-modal__description']}>
                        <Translation id={'enter_a_name_for_the_copy_of_this_configuration'}
                                     defaultMessage={'Enter a name for the copy of this configuration'} />:
                    </p>
                    <input
                        type="text"
                        className="form-control"
                        placeholder={trans(intl, 'name', 'Name')}
                        value={newConfigName}
                        onChange={(e) => setNewConfigName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleCloneConfirm()}
                        autoFocus
                    />
                </Modal>
            )}
        </article>
    )
}

export default ConfigurationCard
